import axios, { isAxiosError } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import i18n from '@/i18n';
import { unwrap } from '@/network/utils';
import { NetworkResponse, netPost } from '../network/useNetwork';

type SupportedFileType =
  | 'text/csv'
  | 'video/3gpp'
  | 'video/mpeg'
  | 'video/quicktime'
  | 'video/mp4'
  | 'image/png'
  | 'image/jpg'
  | 'image/jpeg'
  | 'image/webp'
  | 'application/epub+zip'
  | 'application/pdf'
  | 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const fileTypeMap: Record<SupportedFileType, string> = {
  'text/csv': '.csv',
  'video/3gpp': '.3gp',
  'video/mpeg': '.mpeg',
  'video/quicktime': '.mov',
  'video/mp4': '.mp4',
  'image/png': '.png',
  'image/jpg': '.jpg',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'application/epub+zip': '.epub',
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

export const displayFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} bytes`;
  } else if (bytes < 1048576) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else if (bytes < 1073741824) {
    return `${(bytes / 1048576).toFixed(1)} MB`;
  } else {
    return `${(bytes / 1073741824).toFixed(1)} GB`;
  }
};

export const getFileSuffix = (file: string): string | null => {
  const parts = file.split('.');
  if (parts.length > 1) {
    return parts[parts.length - 1];
  } else {
    return null;
  }
};

const isSupportedFileType = (fileType: string): fileType is SupportedFileType => {
  return fileType in fileTypeMap;
};

const getFileExtensionFromFileType = (fileType: string): string => {
  if (!fileType) throw new Error('fileType parameter is required and cannot be empty');

  if (!isSupportedFileType(fileType)) {
    throw new Error(`file type ${fileType} not supported`);
  }

  return fileTypeMap[fileType];
};

export const isOffice = (url: string): boolean => {
  return url?.endsWith('.pptx') || url?.endsWith('.docx') || url?.endsWith('.xlsx');
};

export const isEPubMime = (mimeType: string): boolean => {
  return mimeType === 'application/epub+zip' || mimeType === 'application/epub';
};

export const isVideo = (mimeType: string): boolean => {
  return mimeType?.includes('video');
};

export const isImageMime = (mimeType: string): boolean => {
  return mimeType?.includes('image');
};

export const isPdfMime = (mimeType: string): boolean => {
  return mimeType === 'application/pdf';
};

export const getFileName = (
  contentType: string,
  suffixIn?: string
): {
  fileName: string;
  key: string;
} => {
  const key = uuidv4();
  let suffix = suffixIn;
  if (!suffixIn) {
    suffix = getFileExtensionFromFileType(contentType);
  }
  const fileName = key + suffix;

  return { fileName, key };
};

const getUploadUrl = (fileName: string, callback: (response: NetworkResponse) => void): void => {
  netPost('uploadUrl', callback, { fileName });
};

export const saveFileContents = (
  originalFileName: string,
  fileName: string,
  contentType: string,
  contents: any,
  callbackProgress: (
    originalFileName: string,
    percentComplete?: number,
    imageUrl?: string,
    message?: string
  ) => void
) => {
  fetch(contents)
    .then((res) => res.blob())
    .then((blob) => {
      getUploadUrl(fileName, (response: NetworkResponse) => {
        const { isSuccess, result, message } = response;
        if (isSuccess) {
          sendFileContents(originalFileName, fileName, result, contentType, blob, callbackProgress);
        } else {
          callbackProgress(fileName, 100, undefined, message);
        }
      });
    });
};

const sendFileContents = async (
  originalFileName: string,
  fileName: string,
  presignedUrl: string,
  fileType: string,
  contents: any,
  callback: (fileName: string, percentComplete: number, imageUrl?: string, message?: string) => void
) => {
  try {
    const headers = {
      'Content-Type': fileType,
      'Content-Disposition': `inline; filename="${originalFileName}"`,
      'Content-Size': contents.length,
    };
    axios
      .put(presignedUrl, contents, {
        headers,
        onUploadProgress: (progressEvent: any) => {
          const { loaded, total } = progressEvent;
          const percentage = Math.floor((loaded * 100) / total);
          callback(fileName, percentage === 100 ? 99 : percentage, undefined, undefined);
        },
      })
      .then((data: any) => {
        callback(fileName, 100, presignedUrl, undefined);
      });
  } catch (err: any) {
    callback(fileName, 100, undefined, err.message);
  }
};

type UploadProgressCallback = (percent: number) => void;

export interface UploadFileArgs {
  fileName: string;
  contentType: SupportedFileType | string;
  file: Blob | File | ArrayBuffer | Uint8Array;
  signal?: AbortSignal;
  onProgress?: UploadProgressCallback;
}

const getUploadUrlAsync = async (fileName: string) => {
  const response = await netPost('uploadUrl', undefined, { fileName });
  return unwrap<string>(response);
};

const putFileToPresignedUrlAsync = async ({
  presignedUrl,
  file,
  contentType,
  fileName,
  signal,
  onProgress,
}: UploadFileArgs & { presignedUrl: string }) => {
  try {
    await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': contentType,
        'Content-Disposition': `inline; filename="${fileName}"`,
      },
      signal,
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          const pct = Math.floor((evt.loaded * 100) / evt.total);
          onProgress(pct === 100 ? 99 : pct);
        }
      },
    });

    if (onProgress) {
      onProgress(100);
    }
  } catch (e) {
    throw new Error(isAxiosError(e) ? e.message : i18n.t('uploadFailed'));
  }
};

export const uploadFileAsync = async ({
  fileName: originalFileName,
  contentType,
  file,
  signal,
  onProgress,
}: UploadFileArgs) => {
  const key = uuidv4();
  let extension: string | undefined;
  try {
    extension = getFileExtensionFromFileType(contentType);
  } catch {
    // Fallback: derive from original file name
    extension = `.${getFileSuffix(originalFileName) || 'bin'}`;
  }

  const fileName = `${key}${extension}`;
  const presignedUrl = await getUploadUrlAsync(fileName);

  await putFileToPresignedUrlAsync({
    presignedUrl,
    file,
    contentType,
    fileName,
    signal,
    onProgress,
  });

  return {
    key,
    fileName,
    contentType,
  };
};
